import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  toast,
} from '@/shared/ui';
import { ApiError } from '@/shared/api/http';
import { useCreateSupportRequest } from './useCreateSupportRequest';

// Mirrors the API limits (POST /support/requests): both fields are trimmed before the checks.
const SUBJECT_MAX = 200;
const MESSAGE_MAX = 5000;

const schema = z.object({
  subject: z
    .string()
    .trim()
    .min(1, 'Введите тему')
    .max(SUBJECT_MAX, `Максимум ${SUBJECT_MAX} символов`),
  message: z
    .string()
    .trim()
    .min(1, 'Опишите ситуацию')
    .max(MESSAGE_MAX, `Максимум ${MESSAGE_MAX} символов`),
});

const RATE_LIMITED_MESSAGE = 'Слишком много обращений. Попробуйте позже.';

type FormInput = z.input<typeof schema>;
type FormValues = z.output<typeof schema>;

export interface SupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupportDialog({ open, onOpenChange }: SupportDialogProps) {
  const create = useCreateSupportRequest();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { subject: '', message: '' },
  });

  async function onSubmit(values: FormValues) {
    try {
      await create.mutateAsync(values);
      toast({ title: 'Обращение отправлено' });
      reset();
      onOpenChange(false);
    } catch (err) {
      let msg = 'Не удалось отправить обращение';
      if (err instanceof ApiError) {
        msg = err.status === 429 ? RATE_LIMITED_MESSAGE : err.message;
      }
      toast({ variant: 'destructive', description: msg });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!isSubmitting) {
          reset();
          onOpenChange(v);
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Написать в поддержку</DialogTitle>
          <DialogDescription>
            Ответим в рабочие часы, обычно в течение 2 часов
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <div className="space-y-1">
            <Label htmlFor="support-subject">Тема</Label>
            <Input
              id="support-subject"
              placeholder="Например: не конвертируется цена"
              aria-invalid={!!errors.subject}
              {...register('subject')}
            />
            {errors.subject && (
              <p className="text-xs text-destructive">{errors.subject.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="support-message">Обращение</Label>
            <Textarea
              id="support-message"
              rows={5}
              placeholder="Опишите ситуацию: источник, ID объявления, что ожидали увидеть"
              aria-invalid={!!errors.message}
              {...register('message')}
            />
            {errors.message && (
              <p className="text-xs text-destructive">{errors.message.message}</p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Отправка…' : 'Отправить'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={isSubmitting}
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Отмена
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
